import React from 'react';
import UserRow from './UserRow';
import OneTimeSection from './OneTimeSection';
import CollapsibleSection from './CollapsibleSection';
import { ADMIN_GROUP_LABEL, ONE_TIME_ROLE } from '../roles';
import { RoleBadge, CompanyTag, YouTag } from './Badges';
import TempPasswordInfo from './TempPasswordInfo';

// The admins of a company, and everything reporting to them. Admins supervise
// as a group rather than individually, so this section *is* the group: the
// admin accounts themselves, each manager as a subsection with their own
// inspectors nested inside it, and then the inspectors who report to the admins
// directly instead of through a manager.
//
// A company that never adds a manager still reads correctly — its inspectors
// simply sit in this section beside the admins, with no empty middle layer to
// scroll past. That arrangement is the point: the manager tier is available,
// not mandatory.
const AdminGroup = ({
  users,
  showCompany,
  passwords = {},
  onDelete,
  canDelete,
  onResetPassword,
  canResetPassword,
  showTempPasswords,
  currentUserId,
  defaultOpen = true,
}) => {
  const admins = users.filter((u) => u.role === 'admin');
  const managers = users.filter((u) => u.role === 'manager');
  const inspectors = users.filter((u) => u.role === 'inspector');
  const oneTimeAccounts = users.filter((u) => u.role === ONE_TIME_ROLE);

  const managerIds = new Set(managers.map((m) => m.id));
  const inspectorsFor = (managerId) => inspectors.filter((i) => i.manager_id === managerId);
  // Reporting to the admins means having no manager — or naming one who isn't
  // here, which is the same thing in practice and shouldn't hide the account.
  const directInspectors = inspectors.filter(
    (i) => !i.manager_id || !managerIds.has(i.manager_id),
  );

  const rowProps = {
    onDelete,
    canDelete,
    onResetPassword,
    canResetPassword,
    showTempPassword: showTempPasswords,
    currentUserId,
  };

  const memberRows = (people) => (
    <ul className="divide-y divide-gray-700/60">
      {people.map((person) => (
        <UserRow
          key={person.id}
          member={person}
          showCompany={showCompany}
          password={passwords[person.id]}
          indent
          {...rowProps}
        />
      ))}
    </ul>
  );

  const actionsFor = (member) => (
    <>
      {showTempPasswords && <TempPasswordInfo member={member} />}
      {onResetPassword && canResetPassword?.(member) && (
        <button
          onClick={() => onResetPassword(member)}
          className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 hover:text-purple-200 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border border-purple-500/20 hover:border-purple-500/40"
        >
          Reset Password
        </button>
      )}
      {onDelete && (canDelete ? canDelete(member) : member.id !== currentUserId) && (
        <button
          onClick={() => onDelete(member)}
          className="bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border border-red-500/20 hover:border-red-500/40"
        >
          Remove
        </button>
      )}
    </>
  );

  const renderManager = (manager) => {
    const reports = inspectorsFor(manager.id);
    return (
      <CollapsibleSection
        key={`mgr-${manager.id}`}
        tone="sub"
        title={manager.email}
        count={reports.length}
        countLabel={reports.length === 1 ? 'inspector' : 'inspectors'}
        badges={
          <>
            <RoleBadge role="manager" />
            {manager.id === currentUserId && <YouTag />}
            {showCompany && <CompanyTag name={manager.company_name} />}
          </>
        }
        right={actionsFor(manager)}
        className="border-t border-gray-700/60"
      >
        {reports.length > 0 ? (
          memberRows(reports)
        ) : (
          <p className="px-12 py-3 text-sm text-gray-500">No inspectors yet.</p>
        )}
      </CollapsibleSection>
    );
  };

  return (
    <CollapsibleSection
      tone="group"
      title={ADMIN_GROUP_LABEL}
      count={admins.length}
      countLabel={admins.length === 1 ? 'admin' : 'admins'}
      defaultOpen={defaultOpen}
      className="border-b border-gray-700 last:border-b-0"
    >
      {admins.length > 0 ? (
        memberRows(admins)
      ) : (
        <p className="px-12 py-3 text-sm text-amber-300/80">
          No admin here yet — anyone without a manager has nobody above them until one is added.
        </p>
      )}

      {managers.map(renderManager)}

      {directInspectors.length > 0 &&
        (managers.length > 0 ? (
          // Only worth a heading once there is a manager tier to tell it apart
          // from. On its own it is simply the team, and gets no label.
          <CollapsibleSection
            tone="sub"
            title={`Reporting to the ${ADMIN_GROUP_LABEL}`}
            count={directInspectors.length}
            countLabel={directInspectors.length === 1 ? 'inspector' : 'inspectors'}
            className="border-t border-gray-700/60"
          >
            {memberRows(directInspectors)}
          </CollapsibleSection>
        ) : (
          memberRows(directInspectors)
        ))}

      <OneTimeSection
        accounts={oneTimeAccounts}
        passwords={passwords}
        showCompany={showCompany}
        onDelete={onDelete}
        canDelete={canDelete}
        onResetPassword={onResetPassword}
        canResetPassword={canResetPassword}
        showTempPasswords={showTempPasswords}
        currentUserId={currentUserId}
      />

      {admins.length === 0 && managers.length === 0 && inspectors.length === 0
        && oneTimeAccounts.length === 0 && (
        <p className="px-6 py-8 text-center text-gray-400">No team members yet.</p>
      )}
    </CollapsibleSection>
  );
};

export default AdminGroup;
